import {
  useEffect,
  useMemo,
  useState
} from 'react';

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function headers() {
  return {
    apikey: SUPABASE_KEY,
    Authorization:
      'Bearer ' + SUPABASE_KEY,
    'Content-Type':
      'application/json'
  };
}

async function rpc(
  name,
  body,
  label
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/${name}`,
    {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify(body)
    }
  );

  const text =
    await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ${text || response.statusText}`
    );
  }

  const result =
    text ? JSON.parse(text) : null;

  if (result?.ok === false) {
    throw new Error(
      result?.message ||
      `${label} failed.`
    );
  }

  return result;
}

async function fetchCommittees(
  session
) {
  const result = await rpc(
    'committee_cms_list',
    {
      p_instructor_id:
        session.instructorId
    },
    'Loading committees'
  );

  return Array.isArray(
    result?.committees
  )
    ? result.committees
    : [];
}

async function fetchPeriodOptions(
  session
) {
  const result = await rpc(
    'committee_cms_period_options',
    {
      p_instructor_id:
        session.instructorId
    },
    'Loading committee periods'
  );

  return Array.isArray(
    result?.semesters
  )
    ? result.semesters
    : [];
}

async function searchInstructors(
  session,
  query
) {
  const clean =
    String(query || '').trim();

  if (!clean) {
    return [];
  }

  const result = await rpc(
    'committee_cms_search_instructors',
    {
      p_instructor_id:
        session.instructorId,
      p_query: clean
    },
    'Searching instructors'
  );

  return Array.isArray(
    result?.instructors
  )
    ? result.instructors
    : [];
}

async function createCommittee(
  session,
  {
    name,
    durationType,
    semesterId,
    academicYearStart,
    memberIds
  }
) {
  return rpc(
    'committee_cms_create',
    {
      p_instructor_id:
        session.instructorId,
      p_name: name,
      p_duration_type:
        durationType,
      p_semester_id:
        semesterId || null,
      p_academic_year_start:
        academicYearStart ||
        null,
      p_member_ids:
        memberIds || []
    },
    'Creating committee'
  );
}

async function deleteCommittee(
  session,
  committeeId
) {
  return rpc(
    'committee_cms_delete',
    {
      p_instructor_id:
        session.instructorId,
      p_committee_id:
        committeeId
    },
    'Deleting committee'
  );
}

async function addMember(
  session,
  committeeId,
  instructorId
) {
  return rpc(
    'committee_cms_add_member',
    {
      p_instructor_id:
        session.instructorId,
      p_committee_id:
        committeeId,
      p_member_instructor_id:
        instructorId
    },
    'Adding committee member'
  );
}

async function removeMember(
  session,
  committeeId,
  instructorId
) {
  return rpc(
    'committee_cms_remove_member',
    {
      p_instructor_id:
        session.instructorId,
      p_committee_id:
        committeeId,
      p_member_instructor_id:
        instructorId
    },
    'Removing committee member'
  );
}

function parseSemesterName(
  name
) {
  const match =
    String(name || '')
      .trim()
      .match(
        /^(Fall|Spring)\s+(\d{4})$/i
      );

  if (!match) {
    return null;
  }

  return {
    season:
      match[1].toLowerCase() ===
      'fall'
        ? 'Fall'
        : 'Spring',
    year:
      Number(match[2])
  };
}

function durationLabel(
  committee
) {
  if (
    committee.duration_type ===
    'permanent'
  ) {
    return 'Permanent';
  }

  if (
    committee.duration_type ===
    'semester'
  ) {
    return (
      committee.semester_name ||
      'Semester'
    );
  }

  if (
    committee.duration_type ===
    'year'
  ) {
    const start =
      committee
        .academic_year_start;

    return start
      ? `${start}–${String(
          start + 1
        ).slice(-2)}`
      : 'Academic Year';
  }

  return '';
}

function MemberPicker({
  session,
  selected,
  onAdd,
  onRemove,
  disabled
}) {
  const [query, setQuery] =
    useState('');

  const [results, setResults] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  useEffect(() => {
    const clean =
      query.trim();

    if (!clean) {
      setResults([]);
      return undefined;
    }

    let cancelled = false;

    const timer =
      window.setTimeout(
        async () => {
          setLoading(true);

          try {
            const rows =
              await searchInstructors(
                session,
                clean
              );

            if (!cancelled) {
              const selectedIds =
                new Set(
                  selected.map(
                    member =>
                      member.id
                  )
                );

              setResults(
                rows.filter(
                  instructor =>
                    !selectedIds.has(
                      instructor.id
                    )
                )
              );
            }
          } catch (err) {
            console.error(
              'Committee instructor search failed:',
              err
            );

            if (!cancelled) {
              setResults([]);
            }
          } finally {
            if (!cancelled) {
              setLoading(false);
            }
          }
        },
        250
      );

    return () => {
      cancelled = true;
      window.clearTimeout(
        timer
      );
    };
  }, [
    query,
    selected,
    session
  ]);

  function choose(
    instructor
  ) {
    onAdd(instructor);
    setQuery('');
    setResults([]);
  }

  return (
    <div>
      <div style={styles.memberList}>
        {selected.map(
          member => (
            <span
              key={member.id}
              style={styles.memberChip}
            >
              {member.name ||
                member.email}

              <button
                type="button"
                style={styles.chipRemove}
                disabled={disabled}
                onClick={() =>
                  onRemove(
                    member.id
                  )
                }
              >
                ×
              </button>
            </span>
          )
        )}
      </div>

      <div style={styles.searchWrap}>
        <input
          style={styles.input}
          type="text"
          value={query}
          disabled={disabled}
          onChange={event =>
            setQuery(
              event.target.value
            )
          }
          placeholder="Start typing a name..."
        />

        {query.trim() && (
          <div
            style={
              styles.searchResults
            }
          >
            {loading ? (
              <div
                style={
                  styles.searchMessage
                }
              >
                Searching...
              </div>
            ) : results.length === 0 ? (
              <div
                style={
                  styles.searchMessage
                }
              >
                No matching instructors.
              </div>
            ) : (
              results.map(
                instructor => (
                  <button
                    key={
                      instructor.id
                    }
                    type="button"
                    style={
                      styles.searchResult
                    }
                    onClick={() =>
                      choose(
                        instructor
                      )
                    }
                  >
                    <strong>
                      {instructor.name ||
                        instructor.email}
                    </strong>

                    {instructor.name &&
                      instructor.email && (
                        <span>
                          {
                            instructor.email
                          }
                        </span>
                      )}
                  </button>
                )
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Committees({
  session
}) {
  const [committees, setCommittees] =
    useState([]);

  const [semesters, setSemesters] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState('');

  const [success, setSuccess] =
    useState('');

  const [showCreate, setShowCreate] =
    useState(false);

  const [name, setName] =
    useState('');

  const [duration, setDuration] =
    useState('permanent');

  const [
    semesterYear,
    setSemesterYear
  ] = useState('');

  const [
    semesterSeason,
    setSemesterSeason
  ] = useState('Fall');

  const [
    academicYear,
    setAcademicYear
  ] = useState('');

  const [
    newMembers,
    setNewMembers
  ] = useState([]);

  const parsedSemesters =
    useMemo(
      () =>
        semesters
          .map(
            semester => ({
              ...semester,
              parsed:
                parseSemesterName(
                  semester.name
                )
            })
          )
          .filter(
            semester =>
              !!semester.parsed
          ),
      [semesters]
    );

  const semesterYears =
    useMemo(
      () =>
        Array.from(
          new Set(
            parsedSemesters.map(
              semester =>
                semester.parsed
                  .year
            )
          )
        ).sort(
          (a, b) => b - a
        ),
      [parsedSemesters]
    );

  const academicYears =
    useMemo(
      () => {
        const starts =
          new Set();

        parsedSemesters.forEach(
          semester => {
            if (
              semester.parsed
                .season === 'Fall'
            ) {
              starts.add(
                semester.parsed
                  .year
              );
            } else {
              starts.add(
                semester.parsed
                  .year - 1
              );
            }
          }
        );

        return Array.from(starts)
          .sort(
            (a, b) => b - a
          );
      },
      [parsedSemesters]
    );

  const selectedSemester =
    parsedSemesters.find(
      semester =>
        semester.parsed.year ===
          Number(
            semesterYear
          ) &&
        semester.parsed.season ===
          semesterSeason
    ) || null;

  async function load() {
    setLoading(true);
    setError('');

    try {
      const [
        committeeRows,
        semesterRows
      ] = await Promise.all([
        fetchCommittees(
          session
        ),
        fetchPeriodOptions(
          session
        )
      ]);

      setCommittees(
        committeeRows
      );

      setSemesters(
        semesterRows
      );

      const parsed =
        semesterRows
          .map(
            semester =>
              parseSemesterName(
                semester.name
              )
          )
          .filter(Boolean);

      if (
        parsed.length &&
        !semesterYear
      ) {
        setSemesterYear(
          String(
            parsed[0].year
          )
        );
      }

      if (
        parsed.length &&
        !academicYear
      ) {
        const first =
          parsed[0];

        setAcademicYear(
          String(
            first.season ===
            'Fall'
              ? first.year
              : first.year - 1
          )
        );
      }
    } catch (err) {
      setError(
        err?.message ||
        'Could not load committees.'
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.instructorId]);

  function resetCreate() {
    setName('');
    setDuration(
      'permanent'
    );
    setNewMembers([]);
    setShowCreate(false);
  }

  function addNewMember(
    instructor
  ) {
    setNewMembers(
      current => [
        ...current,
        instructor
      ]
    );
  }

  function removeNewMember(
    instructorId
  ) {
    setNewMembers(
      current =>
        current.filter(
          member =>
            member.id !==
            instructorId
        )
    );
  }

  async function handleCreate(
    event
  ) {
    event.preventDefault();

    const cleanName =
      name.trim();

    if (!cleanName) {
      setError(
        'Enter a committee name.'
      );
      return;
    }

    if (
      duration ===
        'semester' &&
      !selectedSemester
    ) {
      setError(
        'Choose a configured year and semester.'
      );
      return;
    }

    if (
      duration === 'year' &&
      !Number(academicYear)
    ) {
      setError(
        'Choose an academic year.'
      );
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await createCommittee(
        session,
        {
          name: cleanName,
          durationType:
            duration,
          semesterId:
            duration ===
            'semester'
              ? selectedSemester
                  .id
              : null,
          academicYearStart:
            duration ===
            'year'
              ? Number(
                  academicYear
                )
              : null,
          memberIds:
            newMembers.map(
              member =>
                member.id
            )
        }
      );

      resetCreate();

      setSuccess(
        'Committee created.'
      );

      await load();
    } catch (err) {
      setError(
        err?.message ||
        'Could not create committee.'
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(
    committee
  ) {
    if (
      !window.confirm(
        `Delete ${committee.name}? Its committee meetings will also be deleted.`
      )
    ) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await deleteCommittee(
        session,
        committee.id
      );

      setCommittees(
        current =>
          current.filter(
            item =>
              item.id !==
              committee.id
          )
      );

      setSuccess(
        'Committee deleted.'
      );
    } catch (err) {
      setError(
        err?.message ||
        'Could not delete committee.'
      );
    }
  }

  async function handleAddMember(
    committee,
    instructor
  ) {
    setError('');
    setSuccess('');

    try {
      await addMember(
        session,
        committee.id,
        instructor.id
      );

      setCommittees(
        current =>
          current.map(
            item =>
              item.id ===
              committee.id
                ? {
                    ...item,
                    members: [
                      ...item.members,
                      instructor
                    ].sort(
                      (a, b) =>
                        String(
                          a.name ||
                          a.email ||
                          ''
                        ).localeCompare(
                          String(
                            b.name ||
                            b.email ||
                            ''
                          )
                        )
                    )
                  }
                : item
          )
      );
    } catch (err) {
      setError(
        err?.message ||
        'Could not add committee member.'
      );
    }
  }

  async function handleRemoveMember(
    committee,
    instructorId
  ) {
    setError('');
    setSuccess('');

    try {
      await removeMember(
        session,
        committee.id,
        instructorId
      );

      setCommittees(
        current =>
          current.map(
            item =>
              item.id ===
              committee.id
                ? {
                    ...item,
                    members:
                      item.members.filter(
                        member =>
                          member.id !==
                          instructorId
                      )
                  }
                : item
          )
      );
    } catch (err) {
      setError(
        err?.message ||
        'Could not remove committee member.'
      );
    }
  }

  if (loading) {
    return (
      <div style={styles.muted}>
        Loading committees...
      </div>
    );
  }

  return (
    <div>
      <div
        style={
          styles.headingRow
        }
      >
        <div>
          <h1
            style={styles.heading}
          >
            Committees
          </h1>

          <p style={styles.muted}>
            Create committees and
            manage members from your
            institution.
          </p>
        </div>

        <button
          type="button"
          style={styles.btn}
          onClick={() =>
            setShowCreate(
              current => !current
            )
          }
        >
          {showCreate
            ? 'Cancel'
            : 'New Committee'}
        </button>
      </div>

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      {success && (
        <div style={styles.success}>
          {success}
        </div>
      )}

      {showCreate && (
        <form
          style={styles.createCard}
          onSubmit={
            handleCreate
          }
        >
          <div style={styles.field}>
            <label
              style={styles.label}
            >
              Committee Name
            </label>

            <input
              style={styles.input}
              type="text"
              value={name}
              onChange={event =>
                setName(
                  event.target
                    .value
                )
              }
              autoFocus
            />
          </div>

          <div style={styles.field}>
            <label
              style={styles.label}
            >
              Duration
            </label>

            <div
              style={
                styles.radioGroup
              }
            >
              {[
                [
                  'permanent',
                  'Permanent'
                ],
                [
                  'semester',
                  'Semester'
                ],
                [
                  'year',
                  'Academic Year'
                ]
              ].map(
                ([
                  value,
                  label
                ]) => (
                  <label
                    key={value}
                    style={
                      styles.radioLabel
                    }
                  >
                    <input
                      type="radio"
                      name="duration"
                      value={value}
                      checked={
                        duration ===
                        value
                      }
                      onChange={() =>
                        setDuration(
                          value
                        )
                      }
                    />
                    {label}
                  </label>
                )
              )}
            </div>
          </div>

          {duration ===
            'semester' && (
            <div
              style={
                styles.periodRow
              }
            >
              <div
                style={
                  styles.field
                }
              >
                <label
                  style={
                    styles.label
                  }
                >
                  Year
                </label>

                <select
                  style={
                    styles.input
                  }
                  value={
                    semesterYear
                  }
                  onChange={event =>
                    setSemesterYear(
                      event.target
                        .value
                    )
                  }
                >
                  <option value="">
                    — Select —
                  </option>

                  {semesterYears.map(
                    year => (
                      <option
                        key={year}
                        value={year}
                      >
                        {year}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div
                style={
                  styles.field
                }
              >
                <label
                  style={
                    styles.label
                  }
                >
                  Semester
                </label>

                <select
                  style={
                    styles.input
                  }
                  value={
                    semesterSeason
                  }
                  onChange={event =>
                    setSemesterSeason(
                      event.target
                        .value
                    )
                  }
                >
                  <option value="Fall">
                    Fall
                  </option>

                  <option value="Spring">
                    Spring
                  </option>
                </select>
              </div>
            </div>
          )}

          {duration ===
            'year' && (
            <div style={styles.field}>
              <label
                style={
                  styles.label
                }
              >
                Academic Year
              </label>

              <select
                style={
                  styles.input
                }
                value={
                  academicYear
                }
                onChange={event =>
                  setAcademicYear(
                    event.target
                      .value
                  )
                }
              >
                <option value="">
                  — Select —
                </option>

                {academicYears.map(
                  year => (
                    <option
                      key={year}
                      value={year}
                    >
                      {year}–
                      {String(
                        year + 1
                      ).slice(-2)}
                    </option>
                  )
                )}
              </select>
            </div>
          )}

          <div style={styles.field}>
            <label
              style={styles.label}
            >
              Members
            </label>

            <MemberPicker
              session={session}
              selected={
                newMembers
              }
              onAdd={
                addNewMember
              }
              onRemove={
                removeNewMember
              }
              disabled={saving}
            />
          </div>

          <div
            style={
              styles.actionRow
            }
          >
            <button
              type="button"
              style={
                styles.secondaryBtn
              }
              disabled={saving}
              onClick={
                resetCreate
              }
            >
              Cancel
            </button>

            <button
              style={styles.btn}
              disabled={saving}
            >
              {saving
                ? 'Creating...'
                : 'Create Committee'}
            </button>
          </div>
        </form>
      )}

      {committees.length === 0 ? (
        <div style={styles.empty}>
          No committees.
        </div>
      ) : (
        <div
          style={
            styles.committeeList
          }
        >
          {committees.map(
            committee => (
              <section
                key={
                  committee.id
                }
                style={
                  styles.committeeCard
                }
              >
                <div
                  style={
                    styles.cardHeader
                  }
                >
                  <div>
                    <div
                      style={
                        styles.committeeName
                      }
                    >
                      {
                        committee.name
                      }
                    </div>

                    <div
                      style={
                        styles.durationText
                      }
                    >
                      {durationLabel(
                        committee
                      )}

                      {committee.expires_on
                        ? ` · Ends ${committee.expires_on}`
                        : ''}
                    </div>
                  </div>

                  <button
                    type="button"
                    style={
                      styles.dangerBtn
                    }
                    onClick={() =>
                      handleDelete(
                        committee
                      )
                    }
                  >
                    Delete
                  </button>
                </div>

                <div
                  style={
                    styles.memberHeading
                  }
                >
                  Members
                </div>

                <MemberPicker
                  session={session}
                  selected={
                    committee.members ||
                    []
                  }
                  onAdd={
                    instructor =>
                      handleAddMember(
                        committee,
                        instructor
                      )
                  }
                  onRemove={
                    instructorId =>
                      handleRemoveMember(
                        committee,
                        instructorId
                      )
                  }
                />
              </section>
            )
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  headingRow: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems: 'flex-start',
    gap: '20px',
    marginBottom: '20px'
  },

  heading: {
    margin: '0 0 6px',
    fontSize: '24px',
    color: '#1f2933'
  },

  muted: {
    margin: 0,
    color: '#66788a',
    fontSize: '14px',
    lineHeight: '1.5'
  },

  error: {
    background: '#faeaea',
    border:
      '1px solid #e8bcbc',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#a12a2a',
    fontSize: '14px',
    marginBottom: '16px'
  },

  success: {
    background: '#e7f5ed',
    border:
      '1px solid #b9dfc9',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#216e45',
    fontSize: '14px',
    marginBottom: '16px'
  },

  btn: {
    border: 'none',
    background: '#215b7d',
    color: 'white',
    borderRadius: '8px',
    padding: '9px 15px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer'
  },

  secondaryBtn: {
    border:
      '1px solid #d7dee5',
    background: 'white',
    color: '#66788a',
    borderRadius: '8px',
    padding: '9px 15px',
    fontSize: '14px',
    cursor: 'pointer'
  },

  dangerBtn: {
    border:
      '1px solid #dfb2b2',
    background: 'white',
    color: '#a12a2a',
    borderRadius: '7px',
    padding: '7px 11px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },

  createCard: {
    border:
      '1px solid #c9d7e2',
    borderRadius: '10px',
    background: '#f8fafc',
    padding: '18px',
    marginBottom: '22px'
  },

  field: {
    marginBottom: '14px',
    flex: 1
  },

  label: {
    display: 'block',
    fontWeight: '700',
    fontSize: '13px',
    marginBottom: '6px',
    color: '#1f2933'
  },

  input: {
    width: '100%',
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '9px 10px',
    fontSize: '14px',
    boxSizing:
      'border-box',
    background: 'white'
  },

  radioGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '18px'
  },

  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    fontSize: '14px',
    color: '#42515f',
    cursor: 'pointer'
  },

  periodRow: {
    display: 'grid',
    gridTemplateColumns:
      '1fr 1fr',
    gap: '12px'
  },

  actionRow: {
    display: 'flex',
    justifyContent:
      'flex-end',
    gap: '9px',
    marginTop: '8px'
  },

  committeeList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },

  committeeCard: {
    border:
      '1px solid #d7dee5',
    borderRadius: '10px',
    background: 'white',
    padding: '16px'
  },

  cardHeader: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '14px'
  },

  committeeName: {
    fontSize: '17px',
    fontWeight: '700',
    color: '#1f2933'
  },

  durationText: {
    marginTop: '3px',
    color: '#66788a',
    fontSize: '12px'
  },

  memberHeading: {
    marginBottom: '8px',
    fontSize: '12px',
    fontWeight: '800',
    color: '#42515f',
    textTransform:
      'uppercase',
    letterSpacing:
      '0.05em'
  },

  memberList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '7px',
    marginBottom: '8px'
  },

  memberChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: '#edf4f8',
    border:
      '1px solid #cbdde8',
    borderRadius: '999px',
    padding:
      '5px 7px 5px 10px',
    color: '#294b61',
    fontSize: '13px',
    fontWeight: '600'
  },

  chipRemove: {
    width: '20px',
    height: '20px',
    border: 'none',
    borderRadius: '50%',
    background: 'transparent',
    color: '#66788a',
    cursor: 'pointer',
    fontSize: '16px',
    lineHeight: 1,
    padding: 0
  },

  searchWrap: {
    position: 'relative'
  },

  searchResults: {
    position: 'absolute',
    left: 0,
    right: 0,
    top:
      'calc(100% + 4px)',
    zIndex: 20,
    maxHeight: '240px',
    overflowY: 'auto',
    background: 'white',
    border:
      '1px solid #c9d7e2',
    borderRadius: '8px',
    boxShadow:
      '0 8px 20px rgba(0,0,0,0.12)'
  },

  searchResult: {
    width: '100%',
    border: 'none',
    borderBottom:
      '1px solid #edf1f4',
    background: 'white',
    padding: '9px 11px',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    cursor: 'pointer',
    color: '#1f2933'
  },

  searchMessage: {
    padding: '10px 11px',
    color: '#66788a',
    fontSize: '13px'
  },

  empty: {
    border:
      '1px dashed #c9d7e2',
    borderRadius: '10px',
    padding: '24px',
    textAlign: 'center',
    color: '#66788a',
    fontSize: '14px'
  }
};